import Badge from "@intility/bifrost-react/Badge";
import Button from "@intility/bifrost-react/Button";
import { canSyncNow } from "~/api/companies";
import type { OnboardedCompany } from "~/api/types";
import { rowState } from "./onboarding";
import { ScimControls } from "./ScimControls";
import { SyncNowCell } from "./SyncNowCell";
import { TenantList } from "./TenantList";

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <h3 className="bf-h4" style={{ margin: 0 }}>
        {title}
      </h3>
      {children}
    </section>
  );
}

// The full record of one company: status, domains, and each tenant with its sync state,
// provisioning mode, SCIM tokens and SCIM scope (ADR-0058, ADR-0062).
export function CompanyDetails({
  company,
  onEdit,
}: {
  company: OnboardedCompany;
  onEdit: () => void;
}) {
  const state = rowState(company);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <div>
        <Badge pill state={state.badge}>
          {state.label}
        </Badge>
        <div className="bf-small" style={{ color: "var(--bfc-base-c-2)" }}>
          <code>{company.companyId}</code>
          {company.orgSlug && <> · org {company.orgSlug}</>}
        </div>
        {company.error && (
          <div style={{ color: "var(--bfc-alert)" }}>{company.error}</div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8 }}>
        <Button small variant="basic" onClick={onEdit}>
          {state.action}
        </Button>
        {canSyncNow(company) && <SyncNowCell companyId={company.companyId} />}
      </div>

      <Section title="Domains">
        {company.domains.length === 0 ? (
          <span>No domain</span>
        ) : (
          company.domains.map((d) => <div key={d}>{d}</div>)
        )}
      </Section>

      <Section title="Tenants">
        {company.tenants.length === 0 && <span>No tenant</span>}
        {company.tenants.map((tenant) => (
          <div
            key={tenant.tenantId}
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 16,
              padding: 16,
              border: "1px solid var(--bfc-base-c-dimmed)",
              borderRadius: 4,
            }}
          >
            <TenantList tenants={[tenant]} />
            <ScimControls companyId={company.companyId} tenant={tenant} />
          </div>
        ))}
      </Section>
    </div>
  );
}
